/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Soonki Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Thu Mar 23 2023
 **/

import { HTMLAttributes, forwardRef } from "react";
import { Interpolation, Theme } from "@emotion/react";

import * as S from "./private/emotion";

interface DividerBaseProps extends HTMLAttributes<HTMLDivElement> {
	css?: Interpolation<Theme>;
	children?: React.ReactNode;
}

type DividerProps = TextProps | RoundedTextProps | LineProps | DragProps | VerticalProps;

type LineProps = {
	type: "line";
} & DividerBaseProps;

type DragProps = {
	type: "drag";
} & DividerBaseProps;

type TextProps = {
	type: "text";
	text?: string;
	fill?: string;
} & DividerBaseProps;

type RoundedTextProps = {
	type: "roundedText";
	text?: string;
	fill?: string;
} & DividerBaseProps;

type VerticalProps = {
	type: "vertical";
} & DividerBaseProps;

const Divider = forwardRef<Element, DividerProps>(function Divider(props, ref) {
	const { type, ...rest } = props;

	if (type === "line") {
		return <LineDivider {...rest} />;
	}
	if (type === "drag") {
		return <DragDivider {...rest} ref={ref as React.ForwardedRef<HTMLDivElement>} />;
	}
	if (type === "text") {
		return <TextDivider {...rest} />;
	}
	if (type === "roundedText") {
		return <RoundedTextDivider {...rest} ref={ref as React.ForwardedRef<HTMLDivElement>} />;
	}
	if (type === "vertical") {
		return <VerticalDivider {...rest} ref={ref as React.ForwardedRef<HTMLDivElement>} />;
	}

	return null;
});

export default Divider;

const LineDivider = forwardRef(function LineDivider() {
	return (
		<S.DivideWrap>
			<S.DivideLine />
		</S.DivideWrap>
	);
});

export const TextDivider = forwardRef(function TextDivider(
	{ fill = "white", css, className, ...props }: Omit<TextProps, "type">,
	ref: React.ForwardedRef<HTMLDivElement>
) {
	return (
		<S.DivideWrap css={css} className={className} ref={ref}>
			<S.DivideLine />
			<S.Text {...props} css={[S.fillColor(fill)]} />
		</S.DivideWrap>
	);
});

export const RoundedTextDivider = forwardRef(function RoundedTextDivider(
	{ fill = "white", css, className, ...props }: Omit<RoundedTextProps, "type">,
	ref: React.ForwardedRef<HTMLDivElement>
) {
	return (
		<S.DivideWrap css={css} className={className} ref={ref}>
			<S.DivideLine />
			<S.RoundedText {...props} css={[S.fillColor(fill)]} />
		</S.DivideWrap>
	);
});

export const DragDivider = forwardRef(function DragDivider(
	{ css, className, ...props }: Omit<DragProps, "type">,
	ref: React.ForwardedRef<HTMLDivElement>
) {
	return (
		<S.DivideWrap css={css} className={className} ref={ref}>
			<S.DivideLine css={[S.dragCursorCss]} />
			<S.DragHandle {...props} />
		</S.DivideWrap>
	);
});

export const VerticalDivider = forwardRef(function VerticalDivider(
	{ css, className, ...props }: Omit<DividerProps, "type">,
	ref: React.ForwardedRef<HTMLDivElement>
) {
	return (
		<S.DivideWrap css={[{ width: 0, height: "100%", position: "relative" }, css]} className={className} ref={ref}>
			<div
				style={{
					position: "absolute",
					width: "12px",
					height: "100%"
				}}
			>
				<S.VerticalDivider {...props} />
			</div>
		</S.DivideWrap>
	);
});
